<template>
  <div class="math-function-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="-400 -300 800 600" preserveAspectRatio="xMidYMid meet">
           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="-400 + (i-1)*50" y1="-300" :x2="-400 + (i-1)*50" y2="300" />
              <line v-for="i in 13" :key="'h'+i" x1="-400" :y1="-300 + (i-1)*50" x2="400" :y2="-300 + (i-1)*50" />
           </g>

           <!-- 坐标轴 -->
           <line x1="-400" y1="0" x2="400" y2="0" stroke="#475569" stroke-width="3" />
           <line x1="0" y1="-300" x2="0" y2="300" stroke="#475569" stroke-width="3" />
           
           <text x="360" y="30" fill="#94a3b8" font-size="20" font-weight="900">X轴</text>
           <text x="15" y="-260" fill="#94a3b8" font-size="20" font-weight="900">Y轴</text>

           <!-- 模式 1: 零点与方程 (Zero & Root) -->
           <g v-if="mode === 'zero'">
              <path :d="zeroCurve" fill="none" stroke="#f43f5e" stroke-width="5" stroke-linecap="round" />
              <!-- X轴上的根交点 -->
              <circle :cx="-2.0 * 60" cy="0" r="10" fill="#10b981" stroke="#fff" stroke-width="3" />
              <circle :cx="2.0 * 60" cy="0" r="10" fill="#10b981" stroke="#fff" stroke-width="3" />
              <!-- 动态扫描线 (二分法或寻找解的过程) -->
              <line :x1="currentX * 60" y1="-300" :x2="currentX * 60" y2="300" stroke="#fbbf24" stroke-width="2" stroke-dasharray="8,6" />
              <circle :cx="currentX * 60" :cy="-currentY * 60" r="12" fill="#facc15" stroke="#fff" stroke-width="3" />
              <line :x1="currentX * 60" :y1="-currentY * 60" :x2="currentX * 60" y2="0" stroke="#facc15" stroke-width="3" />
           </g>

           <!-- 模式 2: 极限与连续 (Limit & Continuity) -->
           <g v-else-if="mode === 'limit'">
              <path :d="limitCurveLeft" fill="none" stroke="#38bdf8" stroke-width="5" stroke-linecap="round" />
              <path :d="limitCurveRight" fill="none" stroke="#38bdf8" stroke-width="5" stroke-linecap="round" />
              <!-- 连续 / 跳跃间断点 / 可去间断点 -->
              <circle v-if="limitType === 'continuous'" cx="0" cy="0" r="10" fill="#38bdf8" stroke="#fff" stroke-width="3" />
              <g v-else-if="limitType === 'jump'">
                 <!-- 不连续点 -->
                 <circle cx="0" cy="-60" r="10" fill="#020617" stroke="#38bdf8" stroke-width="4" />
                 <circle cx="0" cy="60" r="10" fill="#38bdf8" stroke="#fff" stroke-width="3" />
                 <line x1="0" y1="-60" x2="0" y2="60" stroke="#f43f5e" stroke-width="3" stroke-dasharray="6,4" />
              </g>
              <g v-else-if="limitType === 'hole'">
                 <circle cx="0" cy="0" r="12" fill="#020617" stroke="#38bdf8" stroke-width="4" />
              </g>
              <!-- 游标探测器 -->
              <circle :cx="currentX * 60" :cy="-currentY * 60" r="10" fill="#facc15" />
              <text :x="currentX*60" :y="-currentY*60 - 20" fill="#f8fafc" font-size="16" font-weight="900" text-anchor="middle">趋近测试</text>
           </g>

           <!-- 模式 3: 迭代与蛛网图 (Cobweb Iteration) -->
           <g v-else-if="mode === 'iterate'">
              <!-- y = x -->
              <line x1="-300" y1="300" x2="300" y2="-300" stroke="#94a3b8" stroke-width="3" stroke-dasharray="10,6" />
              <!-- 曲面 y = f(x) -->
              <path :d="iterateCurve" fill="none" stroke="#a855f7" stroke-width="6" />
              
              <!-- 蛛网连线 -->
              <path :d="cobwebPath" fill="none" stroke="#facc15" stroke-width="3" stroke-linejoin="round" />
              <circle v-for="(pt, idx) in cobwebPoints" :key="idx" :cx="pt.x" :cy="pt.y" r="5" fill="#f43f5e" />
           </g>

           <!-- 模式 4: 基本性质 (Properties/Parity) -->
           <g v-else>
              <path :d="propertyCurve" fill="none" stroke="#10b981" stroke-width="5" />
              <!-- 奇偶性对称测试点 -->
              <circle :cx="currentX * 60" :cy="-currentY * 60" r="10" fill="#f43f5e" />
              <text :x="currentX*60" :y="-currentY*60 - 20" fill="#fda4af" font-size="20" font-weight="900">P(x)</text>

              <!-- 同步关联点 -->
              <circle :cx="-currentX * 60" :cy="-getSymmetricY() * 60" r="10" fill="#38bdf8" />
              <text :x="-currentX*60" :y="-getSymmetricY()*60 - 20" fill="#bae6fd" font-size="20" font-weight="900">P(-x)</text>
              
              <!-- 辅助对称线 -->
              <line v-if="propType === 'odd'" :x1="currentX * 60" :y1="-currentY * 60" :x2="-currentX * 60" :y2="-getSymmetricY() * 60" stroke="#cbd5e1" stroke-dasharray="6,4" stroke-width="2" />
              <line v-if="propType === 'even'" :x1="currentX * 60" :y1="-currentY * 60" :x2="-currentX * 60" :y2="-getSymmetricY() * 60" stroke="#cbd5e1" stroke-dasharray="6,4" stroke-width="2" />
           </g>
        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">逻辑与代数模型</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前状态计算</div>
               <div class="seq-data-list">
                  <template v-if="mode === 'zero'">
                     <div class="data-row"><span>当前 x</span ><span class="hl">{{ currentX.toFixed(2) }}</span></div>
                     <div class="data-row"><span>函数值 f(x)</span ><span class="hl" :class="currentY === 0 ? 'text-green' : 'text-red'">{{ currentY.toFixed(2) }}</span></div>
                     <div class="data-row"><span>状态</span ><span class="hl">{{ Math.abs(currentY) < 0.1 ? '击中零点！' : '寻找中...' }}</span></div>
                  </template>
                  
                  <template v-if="mode === 'limit'">
                     <div class="data-row"><span>游标位置 x</span ><span class="hl">{{ currentX.toFixed(2) }}</span></div>
                     <div class="data-row"><span>当前函数值 f(x)</span ><span class="hl">{{ currentY.toFixed(2) }}</span></div>
                     <div class="data-row"><span>左极限 (x→0⁻)</span ><span class="hl text-blue">{{ limitType === 'jump' ? '1.0' : '0.0' }}</span></div>
                     <div class="data-row"><span>右极限 (x→0⁺)</span ><span class="hl text-red">{{ limitType === 'jump' ? '-1.0' : '0.0' }}</span></div>
                  </template>

                  <template v-if="mode === 'iterate'">
                     <div class="data-row"><span>初始种子 x₀</span ><span class="hl">{{ seedX.toFixed(2) }}</span></div>
                     <div class="data-row"><span>迭代次数 n</span ><span class="hl">{{ iterateCount }}</span></div>
                     <div class="data-row"><span>当前位置 xₙ</span ><span class="hl text-green">{{ currentIterX.toFixed(3) }}</span></div>
                  </template>

                  <template v-if="mode === 'property'">
                     <div class="data-row"><span>目标属性</span ><span class="hl text-green">{{ propType === 'even' ? '偶函数' : '奇函数' }}</span></div>
                     <div class="data-row"><span>f(x)</span ><span class="hl text-red">{{ currentY.toFixed(2) }}</span></div>
                     <div class="data-row"><span>f(-x)</span ><span class="hl text-blue">{{ getSymmetricY().toFixed(2) }}</span></div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2; display: flex; flex-direction: column; width: 100%;">
               <label>知识点模式选择</label>
               <el-radio-group v-model="mode" size="small" class="single-row-buttons">
                 <el-radio-button label="zero">零点方程</el-radio-button>
                 <el-radio-button label="limit">极限连续</el-radio-button>
                 <el-radio-button label="iterate">迭代蛛网</el-radio-button>
                 <el-radio-button label="property">奇偶性质</el-radio-button>
               </el-radio-group>
            </div>
            
            <template v-if="mode === 'zero' || mode === 'limit' || mode === 'property'">
               <div class="s-item" style="grid-column: span 2;">
                  <label>拨动 X 轴自变量探测器</label>
                  <el-slider v-model="currentX" :min="-4" :max="4" :step="0.05" size="small"></el-slider>
               </div>
            </template>
            <template v-if="mode === 'limit'">
               <div class="s-item">
                  <label>间断类型图象切换</label>
                  <el-select v-model="limitType" size="small">
                     <el-option label="完全连续" value="continuous"></el-option>
                     <el-option label="可去间断点 (空洞)" value="hole"></el-option>
                     <el-option label="跳跃间断点" value="jump"></el-option>
                  </el-select>
               </div>
            </template>
            <template v-if="mode === 'iterate'">
               <div class="s-item">
                  <label>迭代初值种子 X₀</label>
                  <el-slider v-model="seedX" :min="-2" :max="2" :step="0.1" size="small"></el-slider>
               </div>
               <div class="s-item">
                  <label>增加迭代深度</label>
                  <el-slider v-model="iterateCount" :min="1" :max="20" :step="1" size="small"></el-slider>
               </div>
            </template>
            <template v-if="mode === 'property'">
               <div class="s-item">
                  <label>性质类型图象切换</label>
                  <el-select v-model="propType" size="small">
                     <el-option label="偶函数 (Y轴对称)" value="even"></el-option>
                     <el-option label="奇函数 (原点对称)" value="odd"></el-option>
                  </el-select>
               </div>
            </template>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MathFunctionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'zero',
      currentX: 1.5,
      limitType: 'jump',
      propType: 'odd',
      seedX: 0.1,
      iterateCount: 3,
      cobwebPoints: [],
      currentIterX: 0
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    },
    seedX() { this.calcCobweb(); },
    iterateCount() { this.calcCobweb(); },
    mode() { this.calcCobweb(); }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'zero') return '函数的零点：将代数方程的根转化为图象与 x 轴的交点';
       if (this.mode === 'limit') return '函数的极限与连续：趋近的哲学与间断的类型';
       if (this.mode === 'iterate') return '函数迭代：用蛛网图揭示动态系统与混沌吸引子的奥秘';
       return '函数的基本性质：深入奇偶对称性与图象单调性';
    },
    activeFormula() {
       if (this.mode === 'zero') return 'f(x) = 0 ⇔ x 为零点';
       if (this.mode === 'limit') return 'lim(x→a) f(x) = L';
       if (this.mode === 'iterate') return 'x_(n+1) = f(x_n)';
       return 'f(-x) = ±f(x)';
    },
    currentY() {
       const x = this.currentX;
       if (this.mode === 'zero') return (Math.pow(x, 2) - 4); // f(x) = x^2 - 4 (roots at +- 2)
       if (this.mode === 'limit') {
          if (this.limitType === 'continuous' || this.limitType === 'hole') return Math.pow(x, 3)/5;
          if (this.limitType === 'jump') return x < 0 ? 1 : -1;
       }
       if (this.mode === 'property') {
          if (this.propType === 'even') return Math.pow(x, 2)/2 - 3; // Even
          if (this.propType === 'odd') return Math.pow(x, 3)/5; // Odd
       }
       return 0;
    },
    zeroCurve() {
       let pts = [];
       for(let x=-5; x<=5; x+=0.1) {
          let y = Math.pow(x,2)-4;
          if (!isNaN(y)) pts.push(`${(x*60).toFixed(1)},${(-y*60).toFixed(1)}`);
       }
       return pts.length > 0 ? 'M ' + pts.join(' L ') : '';
    },
    limitCurveLeft() {
       let pts = [];
       for(let x=-5; x<=-0.01; x+=0.1) {
          let y = (this.limitType==='jump') ? 1 : Math.pow(x,3)/5;
          if (!isNaN(y)) pts.push(`${(x*60).toFixed(1)},${(-y*60).toFixed(1)}`);
       }
       return pts.length > 0 ? 'M ' + pts.join(' L ') : '';
    },
    limitCurveRight() {
       let pts = [];
       for(let x=0.01; x<=5; x+=0.1) {
          let y = (this.limitType==='jump') ? -1 : Math.pow(x,3)/5;
          if (!isNaN(y)) pts.push(`${(x*60).toFixed(1)},${(-y*60).toFixed(1)}`);
       }
       return pts.length > 0 ? 'M ' + pts.join(' L ') : '';
    },
    iterateCurve() {
       let pts = [];
       for(let x=-4; x<=4; x+=0.1) {
          let y = 2.8 * x * (1 - Math.abs(x*0.25));
          if (!isNaN(y)) pts.push(`${(x*60).toFixed(1)},${(-y*60).toFixed(1)}`);
       }
       return pts.length > 0 ? 'M ' + pts.join(' L ') : '';
    },
    propertyCurve() {
       let pts = [];
       for(let x=-5; x<=5; x+=0.1) {
          let y = (this.propType === 'even') ? (Math.pow(x,2)/2 - 3) : (Math.pow(x,3)/5);
          if (!isNaN(y)) pts.push(`${(x*60).toFixed(1)},${(-y*60).toFixed(1)}`);
       }
       return pts.length > 0 ? 'M ' + pts.join(' L ') : '';
    },
    cobwebPath() {
       if (!this.cobwebPoints || this.cobwebPoints.length < 2) return '';
       const validPts = this.cobwebPoints.filter(p => !isNaN(p.x) && !isNaN(p.y));
       if (validPts.length < 2) return '';
       return 'M ' + validPts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ');
    },
    tipDesc() {
       if (this.mode === 'zero') return '求解方程 f(x) = 0 在代数上可能非常困难，但在几何图像上就是极其直观的视觉动作：即寻找函数曲线跨过贯穿 X 轴的那个接触点！这就是伟大的“数形结合”思想，也是二分法求根的理论基础。';
       if (this.mode === 'limit') return '极限(Lim)描述的是自变量趋近某个点时，函数值趋近的靶点。在图中你可以看到，若是“跳跃间断点”，左右趋近跑向不同的悬崖；若是“可去间断点(空洞)”，虽然那个点本身没有定义，但也拥有确定的极限门槛值。';
       if (this.mode === 'iterate') return '函数迭代将上一次运算的结果带回自身继续运算！图中以 y=x 作为反弹反射镜，垂线打到函数曲线上获取预测值，再打向 y=x 线将其弹射回 x 轴作为新的种子。通过多步迭代，我们能清晰看到它最终是陷入了死循环、收敛于定点、还是爆发出混沌。';
       return '奇函数具有强烈的原点对称性质，满足 f(-x) = -f(x)；而偶函数具有优美的 Y 轴镜像对称性质，满足 f(-x) = f(x)；通过图形对称能极大削减分析计算难度，这在微积分计算定积分时尤为重要！';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/零点|方程/.test(kp)) this.mode = 'zero';
      else if (/极限|连续/.test(kp)) this.mode = 'limit';
      else if (/迭代/.test(kp)) this.mode = 'iterate';
      else if (/性质|奇偶|单调/.test(kp)) this.mode = 'property';
      else this.mode = 'zero';
      this.calcCobweb();
    },
    getSymmetricY() {
       const x = -this.currentX;
       if (this.propType === 'even') return Math.pow(x, 2)/2 - 3;
       return Math.pow(x, 3)/5;
    },
    calcCobweb() {
       if (this.mode !== 'iterate') return;
       let pts = [];
       let cx = this.seedX;
       pts.push({ x: cx*60, y: 0 });
       
       for (let i=0; i<this.iterateCount; i++) {
          let cy = 2.8 * cx * (1 - Math.abs(cx*0.25));
          pts.push({ x: cx*60, y: -cy*60 }); // Up to curve
          pts.push({ x: cy*60, y: -cy*60 }); // Reflect across y=x
          cx = cy;
       }
       this.currentIterX = cx;
       this.cobwebPoints = pts;
    }
  }
}
</script>

<style scoped lang="scss">
.math-function-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #fb7185; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
.single-row-buttons { display: flex; flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; &::-webkit-scrollbar { height: 0; } .el-radio-button { flex: 1; :deep(.el-radio-button__inner) { width: 100%; padding: 8px 10px; font-size: 12px; } } }
</style>
